import { Link, useParams } from '@tanstack/react-router';
import { Plus, Repeat } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, ApiError, call, type Schemas } from '../../api/client';
import { useAction, useMembers, usePriorities, useSeries, useSeriesHistory, useSeriesList, useTasks } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Modal, PageHeader, Select, Spinner, Textarea } from '../../components/ui';
import { formatDay, MONTHS, scheduleSummary, timeAgo, WEEKDAY_LABEL, WEEKDAYS } from '../../lib/format';
import { TaskList } from './TaskCard';

type Series = Schemas['SeriesResponse'];
type ScheduleBody = Schemas['ScheduleDto'];

export function SeriesListPage() {
  const list = useSeriesList().data;
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader title="Recurring tasks" subtitle="Household work that comes back: garbage night, feeding the dog, cleaning the bathroom." actions={<Button onClick={() => setCreating(true)}><Plus size={16} /> New</Button>} />
      {!list ? <Spinner /> : list.length === 0 ? <EmptyState title="No recurring tasks yet" /> : (
        <div className="space-y-2">
          {list.map((s) => (
            <Link key={s.id} to="/recurring/$seriesId" params={{ seriesId: s.id }}>
              <Card className="flex items-center gap-3 hover:bg-[var(--surface-2)]">
                <Repeat size={18} className="text-[var(--muted)]" />
                <div className="flex-1">
                  <p className="font-medium">{s.title}</p>
                  <p className="text-xs text-[var(--muted)]">{scheduleSummary(s.schedule)}{s.standingAssignee ? ` · ${s.standingAssignee.displayName}` : ' · House Pool'}</p>
                </div>
                {s.status === 'PAUSED' ? <Badge tone="warn">Paused</Badge> : null}
              </Card>
            </Link>
          ))}
        </div>
      )}
      {creating ? <SeriesFormDialog onClose={() => setCreating(false)} /> : null}
    </>
  );
}

type Frequency = ScheduleBody['frequency'];
const UNIT: Record<Frequency, string> = { DAILY: 'day(s)', WEEKLY: 'week(s)', MONTHLY: 'month(s)', YEARLY: 'year(s)' };

/** Schedule builder: "Does this have to happen on a specific day?" (docs/recurring-tasks.md §1.2). */
export function ScheduleFields({ initial, startsOn }: { initial?: Schemas['ScheduleResponse']; startsOn?: string }) {
  const [frequency, setFrequency] = useState<Frequency>((initial?.frequency as Frequency) ?? 'WEEKLY');
  const [fixed, setFixed] = useState(initial ? initial.timing === 'FIXED_DAY' : false);
  const [days, setDays] = useState<string[]>(initial?.weekdays ?? []);
  const toggle = (d: string) => setDays((cur) => (fixed ? (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]) : cur.includes(d) ? [] : [d]));
  return (
    <div className="space-y-3 rounded-xl bg-[var(--surface-2)] p-3">
      <input type="hidden" name="frequency" value={frequency} />
      <input type="hidden" name="timing" value={frequency === 'DAILY' || fixed ? 'FIXED_DAY' : 'FLEXIBLE'} />
      <input type="hidden" name="weekdays" value={days.join(',')} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Repeats">
          {(id) => (
            <Select id={id} value={frequency} onChange={(e) => setFrequency(e.target.value as Frequency)}>
              <option value="DAILY">Daily</option><option value="WEEKLY">Weekly</option><option value="MONTHLY">Monthly</option><option value="YEARLY">Yearly</option>
            </Select>
          )}
        </Field>
        <Field label={`Every … ${UNIT[frequency]}`}>{(id) => <Input id={id} name="interval" type="number" min={1} max={12} defaultValue={initial?.interval ?? 1} />}</Field>
      </div>
      {frequency !== 'DAILY' ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={fixed} onChange={(e) => { setFixed(e.target.checked); setDays([]); }} />
          It has to happen on a specific day (like garbage pickup)
        </label>
      ) : null}
      {frequency === 'WEEKLY' ? (
        <div>
          <p className="mb-1 text-sm font-medium">{fixed ? 'On' : 'Ideally on (optional)'}</p>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((d) => (
              <button key={d} type="button" onClick={() => toggle(d)} className={`rounded-lg px-2.5 py-1 text-sm ${days.includes(d) ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface)]'}`}>
                {WEEKDAY_LABEL[d]}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {frequency === 'YEARLY' ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Month">
            {(id) => (
              <Select id={id} name="monthOfYear" defaultValue={initial?.monthOfYear ?? Number((startsOn ?? new Date().toLocaleDateString('en-CA')).slice(5, 7))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </Select>
            )}
          </Field>
          <Field label={fixed ? 'Day' : 'Ideally on day (optional)'} hint="-1 = the last day of the month.">
            {(id) => <Input id={id} name="monthDay" type="number" min={-1} max={31} defaultValue={initial?.monthDay ?? ''} required={fixed} />}
          </Field>
        </div>
      ) : null}
      {frequency === 'MONTHLY' ? (
        <Field label={fixed ? 'Day of the month' : 'Ideally on day (optional)'} hint="Use -1 for the last day of the month.">
          {(id) => <Input id={id} name="monthDay" type="number" min={-1} max={31} defaultValue={initial?.monthDay ?? ''} required={fixed} />}
        </Field>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Starts">{(id) => <Input id={id} name="startsOn" type="date" required defaultValue={initial?.startsOn ?? startsOn ?? new Date().toLocaleDateString('en-CA')} />}</Field>
        <Field label="Ends (optional)">{(id) => <Input id={id} name="endsOn" type="date" defaultValue={initial?.endsOn ?? ''} />}</Field>
      </div>
    </div>
  );
}

export function readSchedule(f: FormData): ScheduleBody {
  const weekdays = String(f.get('weekdays') || '').split(',').filter(Boolean) as ScheduleBody['weekdays'];
  return {
    frequency: f.get('frequency') as ScheduleBody['frequency'],
    interval: Number(f.get('interval') || 1),
    timing: f.get('timing') as ScheduleBody['timing'],
    ...(weekdays?.length ? { weekdays } : {}),
    ...(f.get('monthDay') ? { monthDay: Number(f.get('monthDay')) } : {}),
    ...(f.get('frequency') === 'YEARLY' ? { monthOfYear: Number(f.get('monthOfYear')) } : {}),
    startsOn: String(f.get('startsOn')),
    endsOn: (f.get('endsOn') as string) || null,
  };
}

function SeriesFormDialog({ onClose, series }: { onClose(): void; series?: Series }) {
  const { familyId } = useHousehold();
  const priorities = usePriorities().data ?? [];
  const save = useAction(
    (f: FormData) => {
      const content = {
        title: String(f.get('title')),
        description: (f.get('description') as string) || null,
        priority: String(f.get('priority')),
        points: f.get('points') ? Number(f.get('points')) : null,
      };
      if (series) {
        return call(api.PATCH('/api/v1/families/{familyId}/series/{seriesId}', { params: { path: { familyId, seriesId: series.id } }, body: { expectedVersion: series.version, ...content, schedule: readSchedule(f) } }));
      }
      return call(
        api.POST('/api/v1/families/{familyId}/series', {
          params: { path: { familyId }, header: { 'Idempotency-Key': crypto.randomUUID() } },
          body: { ...content, schedule: readSchedule(f), standing: f.get('mine') ? 'ME' : 'POOL' },
        }),
      );
    },
    { success: series ? 'Saved' : 'Recurring task created' },
  );
  const material = save.error instanceof ApiError && save.error.code === 'MATERIAL_CHANGE_REQUIRES_APPROVAL';
  return (
    <Modal open onOpenChange={onClose} title={series ? 'Edit recurring task' : 'New recurring task'}>
      <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); save.mutate(new FormData(e.currentTarget), { onSuccess: onClose }); }}>
        <Field label="What needs doing?">{(id) => <Input id={id} name="title" required maxLength={200} defaultValue={series?.title} placeholder="Take the garbage out" />}</Field>
        <Field label="Details (optional)">{(id) => <Textarea id={id} name="description" maxLength={4000} defaultValue={series?.description ?? ''} />}</Field>
        <ScheduleFields initial={series?.schedule} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">{(id) => <Select id={id} name="priority" key={priorities.length} defaultValue={series?.priority.code ?? priorities.find((p) => p.isDefault)?.code ?? 'NORMAL'}>{priorities.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}</Select>}</Field>
          <Field label="Points for kids">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={series?.points ?? ''} />}</Field>
        </div>
        {!series ? <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="mine" /> I'll always do this one</label> : null}
        {material ? (
          <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">This is a child's chore. Changing what they're asked to do needs your partner's approval — use "Propose a change" on the recurring task instead.</p>
        ) : <ErrorText error={save.error} />}
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={save.isPending}>Save</Button></div>
      </form>
    </Modal>
  );
}

export function SeriesDetailPage() {
  const { seriesId } = useParams({ from: '/app/recurring/$seriesId' });
  const { familyId } = useHousehold();
  const s = useSeries(seriesId).data;
  const occurrences = useTasks({ seriesId, status: 'open', sort: 'due' });
  const history = useSeriesHistory(seriesId).data?.items ?? [];
  const children = (useMembers().data ?? []).filter((m) => m.role === 'CHILD');
  const [dialog, setDialog] = useState<null | 'edit' | 'child' | 'end-child'>(null);
  const path = { params: { path: { familyId, seriesId } } };
  const act = useAction((action: 'pause' | 'resume' | 'claim' | 'release' | 'end') => {
    if (action === 'pause') return call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/pause', path));
    if (action === 'resume') return call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/resume', path));
    if (action === 'claim') return call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/standing-assignment/claim', path));
    if (action === 'release') return call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/standing-assignment/release', path));
    return call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/end', { ...path, body: { openOccurrences: 'ARCHIVE_UNSTARTED_POOL' } }));
  });
  const proposeChild = useAction(
    (f: FormData) =>
      call(
        api.POST('/api/v1/families/{familyId}/series/{seriesId}/child-delegations', {
          ...path,
          body: {
            childMemberId: String(f.get('child')),
            title: String(f.get('title')),
            description: (f.get('description') as string) || null,
            points: f.get('points') ? Number(f.get('points')) : null,
            schedule: readSchedule(f),
            note: (f.get('note') as string) || undefined,
            ...(s?.childAssignmentId ? { amendsChildAssignmentId: s.childAssignmentId } : {}),
          },
        }),
      ),
    { success: 'Proposal sent' },
  );
  const endChild = useAction(
    (f: FormData) =>
      call(api.POST('/api/v1/families/{familyId}/series/{seriesId}/child-assignment/end', { ...path, body: { reason: (f.get('reason') as string) || undefined, openOccurrences: f.get('keep') ? 'KEEP_WITH_CHILD' : 'TO_POOL' } })),
    { success: 'Arrangement ended' },
  );
  if (!s) return <Spinner />;
  const can = (a: Series['allowedActions'][number]) => s.allowedActions.includes(a);
  return (
    <>
      <PageHeader
        title={s.title}
        subtitle={<>{scheduleSummary(s.schedule)} · {s.standingAssignee ? `${s.standingAssignee.displayName} does this` : 'goes to the House Pool'}{s.status !== 'ACTIVE' ? ` · ${s.status.toLowerCase()}` : ''}</>}
      />
      <div className="mb-5 flex flex-wrap gap-2">
        {can('CLAIM_STANDING') ? <Button onClick={() => act.mutate('claim')} busy={act.isPending}>I'll always do this</Button> : null}
        {can('PROPOSE_CHILD_ASSIGNMENT') ? <Button variant="secondary" onClick={() => setDialog('child')}>Give to a child every time…</Button> : null}
        {can('PROPOSE_AMENDMENT') ? <Button variant="secondary" onClick={() => setDialog('child')}>Propose a change…</Button> : null}
        {can('END_CHILD_ASSIGNMENT') ? <Button variant="secondary" onClick={() => setDialog('end-child')}>End {s.standingAssignee?.displayName}'s chore…</Button> : null}
        {can('EDIT') ? <Button variant="ghost" onClick={() => setDialog('edit')}>Edit</Button> : null}
        {can('RELEASE_STANDING') ? <Button variant="ghost" onClick={() => act.mutate('release')}>Stop always doing this</Button> : null}
        {can('PAUSE') ? <Button variant="ghost" onClick={() => act.mutate('pause')}>Pause</Button> : null}
        {can('RESUME') ? <Button variant="ghost" onClick={() => act.mutate('resume')}>Resume</Button> : null}
        {can('END') ? <Button variant="ghost" onClick={() => { if (confirm('End this recurring task? Nothing new will be created.')) act.mutate('end'); }}>End</Button> : null}
      </div>
      {s.upcoming.length ? <p className="mb-4 text-sm text-[var(--muted)]">Next: {s.upcoming.slice(0, 3).map((u) => (u.preferredOn ?? u.dueOn === u.occurrenceOn ? formatDay(u.preferredOn ?? u.dueOn) : `week of ${formatDay(u.windowStartsOn)}`)).join(' · ')}</p> : null}
      <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">Open</h2>
      <TaskList tasks={occurrences.data?.items} empty={<EmptyState title="Nothing open right now" />} />
      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-[var(--muted)]">History</h2>
        <ol className="space-y-2 border-l border-[var(--border)] pl-4 text-sm">
          {history.map((e) => <li key={e.id}>{e.text} <span className="text-xs text-[var(--muted)]">· {timeAgo(e.occurredAt)}</span></li>)}
        </ol>
      </section>

      {dialog === 'edit' ? <SeriesFormDialog series={s} onClose={() => setDialog(null)} /> : null}
      {dialog === 'child' ? (
        <Modal open onOpenChange={() => setDialog(null)} title={s.childAssignmentId ? 'Propose a change' : 'Give to a child every time'}>
          <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); proposeChild.mutate(new FormData(e.currentTarget), { onSuccess: () => setDialog(null) }); }}>
            <p className="text-sm text-[var(--muted)]">Once your partner approves, every occurrence goes to the child automatically.{s.childAssignmentId ? ' The current arrangement stays in place until the change is approved.' : ''}</p>
            <Field label="Child">{(id) => <Select id={id} name="child" defaultValue={s.standingAssignee?.memberId}>{children.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}</Select>}</Field>
            <Field label="What the child is asked to do">{(id) => <Input id={id} name="title" required maxLength={200} defaultValue={s.title} />}</Field>
            <Field label="Instructions">{(id) => <Textarea id={id} name="description" maxLength={4000} defaultValue={s.description ?? ''} />}</Field>
            <ScheduleFields initial={s.schedule} />
            <Field label="Points each time">{(id) => <Input id={id} name="points" type="number" min={0} max={1000} defaultValue={s.points ?? ''} />}</Field>
            <Field label="Note to your partner (optional)">{(id) => <Textarea id={id} name="note" maxLength={1000} />}</Field>
            <ErrorText error={proposeChild.error} />
            <Button type="submit" busy={proposeChild.isPending}>Propose</Button>
          </form>
        </Modal>
      ) : null}
      {dialog === 'end-child' ? (
        <Modal open onOpenChange={() => setDialog(null)} title={`End ${s.standingAssignee?.displayName}'s recurring chore`}>
          <form className="space-y-4" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); endChild.mutate(new FormData(e.currentTarget), { onSuccess: () => setDialog(null) }); }}>
            <p className="text-sm text-[var(--muted)]">Either parent can stop a child's chore. Future occurrences go to the House Pool and your partner is told.</p>
            <Field label="Reason (optional)">{(id) => <Input id={id} name="reason" maxLength={500} />}</Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" name="keep" /> Let them finish the current one</label>
            <ErrorText error={endChild.error} />
            <Button type="submit" variant="danger" busy={endChild.isPending}>End arrangement</Button>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
